import React, { useContext, useEffect } from 'react';
import propTypes from 'prop-types';
import { extend, QueryContext } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { CustomerContext } from '@thd-olt-functional/customer-information';
import { LayoutRenderer } from '@thd-olt-component-react/layout-renderer';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { useLocation } from '@thd-olt-component-react/router';
import { useUrlTmxId } from '../../hooks/useUrlTmxId';
import { componentOptions } from './component-options';
import {
  CUSTOMER_TYPES,
  createContentInformationApiErrorHandler,
  getGuestUserId,
  getHydrationOptions,
  getMarketingCloudVisitorID,
  getSvocId,
  getUserId,
  initializeThdOnlineChannelCookie,
  triggerHomepageLoadEvent,
  userAuthorizationStatus
} from '../../utils';
import {
  guestB2cClientSideDataModel,
  guestB2cServerSideDataModel,
  homepagePreviewByIDDataModelClientSide,
  homepagePreviewByIDDataModelServerSide
} from '../../datamodels/home';
import { components } from '../../components/components';
import { usePreviewByID } from '../../hooks';
import { HomepageContext, HomepageProvider } from '../../components/context';
import { HomepageMetadataContainer } from '../../components/metadata/HomepageMetadataContainer';

const { string, bool } = propTypes;

export const GuestHomepage = ({ slug, thdSEOBotDetection }) => {
  declareContexts([QueryContext, ExperienceContext, HomepageContext, CustomerContext], { freeze: true });
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const guestHPBackup = useConfigService('guestHPBackup');
  const store = useStore({ varnish: false });
  const userState = {
    userId: getSvocId() || getUserId(),
    mcvisID: getMarketingCloudVisitorID(),
    userAuthStatus: userAuthorizationStatus(),
    guestUserId: getGuestUserId(),
  };

  const { guestUserId, mcvisID, userAuthStatus } = userState;
  const contentfulPreviewId = usePreviewByID();
  const { search: querystring, pathname } = useLocation();

  const pathWithQueryParams = `${pathname}${querystring}`;
  const isPreviewPage = pathWithQueryParams.indexOf('render*') > -1
    || pathWithQueryParams.indexOf('contentPV') > -1
    || pathWithQueryParams.indexOf('bassPV') > -1;

  let layoutDataModel = {
    server: extend({}, guestB2cServerSideDataModel),
    client: guestB2cClientSideDataModel
  };
  /**
   * This is not a class in the CSS sense. It's referring
   * to the type of layout we have in Contentful, which
   * at the time of writing seems to be a choice between
   * PersonalizedHomepage and EducationalLayout.
   */
  let layoutClass;

  if (contentfulPreviewId) {
    layoutDataModel = {
      server: homepagePreviewByIDDataModelServerSide,
      client: homepagePreviewByIDDataModelClientSide
    };
    layoutClass = 'PersonalizedHomePage';
  }

  useEffect(() => {
    triggerHomepageLoadEvent({ eventName: 'guest-homepage' });
    initializeThdOnlineChannelCookie(channel, isConsumerApp);
  }, []);

  return (
    <HomepageProvider
      value={{
        thdSEOBotDetection,
        hydrationOptions: getHydrationOptions(channel, isConsumerApp),
        customerType: CUSTOMER_TYPES.GUEST,
        urlTmxId: useUrlTmxId(),
        guestUserId,
        mcvisID,
        store,
        userAuthStatus,
        isB2C: true
      }}
    >
      <LayoutRenderer
        components={components}
        componentOptions={componentOptions}
        slug={slug}
        contentfulPreviewId={contentfulPreviewId}
        layoutClass={layoutClass}
        layoutDataModel={layoutDataModel}
        onContentInformationApiError={createContentInformationApiErrorHandler({
          component: 'GuestHomepage',
          redirectTarget: guestHPBackup
        })}
        ssr
        renderMetadata={({ preloadedImages, metadata }) => {
          return (
            <HomepageMetadataContainer
              isPreviewPage={isPreviewPage}
              preloadedImages={preloadedImages}
              metadata={metadata}
            />
          );
        }}
        customerType={CUSTOMER_TYPES.GUEST}
        enableB2CPaddingAndGap
      />
    </HomepageProvider>
  );
};

GuestHomepage.propTypes = {
  slug: string,
  thdSEOBotDetection: bool
};
GuestHomepage.defaultProps = {
  slug: undefined,
  thdSEOBotDetection: false
};

GuestHomepage.displayName = 'GuestHomepage';
